/**
 * product-knowledge-tree.ts
 *
 * 将扁平的产品规格列表组织成层次化知识树（类 PageIndex 思想）。
 * 树结构：root → competitor → series → product
 *
 * 核心优势：
 * - 不需要向量数据库，结构化数据天然是树
 * - 每个节点有自然语言 summary，可直接放进 LLM context
 * - 支持推理式导航和竞品比较查询
 */

import type { PriceIntelProductSpec } from '../types';

// ---------------------------------------------------------------------------
// 知识树节点类型
// ---------------------------------------------------------------------------
export type KnowledgeNodeLevel = 'root' | 'competitor' | 'series' | 'product';

export interface ProductKnowledgeNode {
  node_id: string;
  level: KnowledgeNodeLevel;
  title: string;
  summary: string;
  metadata: Record<string, string | number | null>;
  children: ProductKnowledgeNode[];
  /** 仅叶子节点有 */
  product_spec?: PriceIntelProductSpec;
}

// ---------------------------------------------------------------------------
// 构建知识树
// ---------------------------------------------------------------------------
export function buildProductTree(specs: PriceIntelProductSpec[]): ProductKnowledgeNode {
  if (specs.length === 0) {
    return {
      node_id: 'root',
      level: 'root',
      title: '竞品产品知识库',
      summary: '暂无产品数据',
      metadata: { product_count: 0, competitor_count: 0 },
      children: [],
    };
  }

  const byCompetitor = groupBy(specs, (s) => s.competitor || 'Unknown');

  const competitorNodes: ProductKnowledgeNode[] = Object.entries(byCompetitor)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([comp, compSpecs]) => {
      const bySeries = groupBy(compSpecs, (s) => s.series || '未分类');

      const seriesNodes: ProductKnowledgeNode[] = Object.entries(bySeries)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([series, seriesSpecs]) => {
          const productNodes: ProductKnowledgeNode[] = seriesSpecs
            .sort((a, b) => (a.pitch_mm ?? 99) - (b.pitch_mm ?? 99))
            .map((spec) => ({
              node_id: spec.temp_id,
              level: 'product' as const,
              title: spec.model,
              summary: buildSpecSummary(spec),
              metadata: {
                pitch_mm: spec.pitch_mm ?? null,
                brightness: spec.brightness ?? null,
                led_type: spec.led_type ?? null,
                indoor_outdoor: spec.indoor_outdoor ?? null,
              },
              children: [],
              product_spec: spec,
            }));

          return {
            node_id: `series-${comp}-${series}`,
            level: 'series' as const,
            title: series,
            summary: buildSeriesSummary(series, seriesSpecs),
            metadata: {
              product_count: seriesSpecs.length,
              pitch_range: pitchRange(seriesSpecs),
              indoor_outdoor: dominantValue(seriesSpecs, 'indoor_outdoor'),
            },
            children: productNodes,
          };
        });

      return {
        node_id: `comp-${comp}`,
        level: 'competitor' as const,
        title: comp,
        summary: buildCompetitorSummary(comp, compSpecs),
        metadata: {
          product_count: compSpecs.length,
          series_count: Object.keys(bySeries).length,
          pitch_range: pitchRange(compSpecs),
        },
        children: seriesNodes,
      };
    });

  return {
    node_id: 'root',
    level: 'root',
    title: '竞品产品知识库',
    summary: `共 ${specs.length} 款产品，来自 ${competitorNodes.length} 家竞品。Pitch 范围 ${pitchRange(specs)}。`,
    metadata: {
      product_count: specs.length,
      competitor_count: competitorNodes.length,
    },
    children: competitorNodes,
  };
}

// ---------------------------------------------------------------------------
// 树搜索：按关键词过滤（保留匹配路径上的祖先节点）
// ---------------------------------------------------------------------------
export function searchTree(
  node: ProductKnowledgeNode,
  query: string,
): ProductKnowledgeNode | null {
  const q = query.toLowerCase();

  // 叶子节点：直接匹配
  if (node.level === 'product') {
    const matchText = [node.title, node.summary, ...Object.values(node.metadata)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return matchText.includes(q) ? node : null;
  }

  // 非叶子节点：递归过滤 children
  const matchedChildren = node.children
    .map((child) => searchTree(child, query))
    .filter((c): c is ProductKnowledgeNode => c !== null);

  // 节点自身标题也匹配
  const selfMatch = node.title.toLowerCase().includes(q) || node.summary.toLowerCase().includes(q);

  if (matchedChildren.length > 0 || selfMatch) {
    return {
      ...node,
      children: matchedChildren.length > 0 ? matchedChildren : node.children,
    };
  }

  return null;
}

// ---------------------------------------------------------------------------
// 导出树为 JSON（可直接喂给 LLM 做推理式检索）
// ---------------------------------------------------------------------------
export function treeToLLMContext(node: ProductKnowledgeNode, maxDepth = 3, depth = 0): string {
  if (depth >= maxDepth) return '';

  const indent = '  '.repeat(depth);
  const lines: string[] = [];

  lines.push(`${indent}[${node.level}] ${node.title}`);
  lines.push(`${indent}  摘要: ${node.summary}`);

  if (node.level === 'product' && node.product_spec) {
    // 产品节点展示完整参数
    const spec = node.product_spec;
    const params = [
      spec.pitch_mm && `Pitch: ${spec.pitch_mm}mm`,
      spec.led_type && `LED: ${spec.led_type}`,
      spec.panel_width_mm && spec.panel_height_mm && `箱体: ${spec.panel_width_mm}×${spec.panel_height_mm}mm`,
      spec.weight_kg && `重量: ${spec.weight_kg}kg`,
      spec.max_power_w && `功耗: ${spec.max_power_w}W`,
      spec.ip_rating && `IP: ${spec.ip_rating}`,
      spec.refresh_rate && `刷新率: ${spec.refresh_rate}Hz`,
    ].filter(Boolean);
    lines.push(`${indent}  参数: ${params.join(' | ')}`);
  }

  for (const child of node.children) {
    const childText = treeToLLMContext(child, maxDepth, depth + 1);
    if (childText) lines.push(childText);
  }

  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// 统计
// ---------------------------------------------------------------------------
export function getTreeStats(node: ProductKnowledgeNode): {
  totalProducts: number;
  totalCompetitors: number;
  totalSeries: number;
} {
  let totalProducts = 0;
  let totalCompetitors = 0;
  let totalSeries = 0;

  function walk(n: ProductKnowledgeNode) {
    if (n.level === 'product') totalProducts++;
    if (n.level === 'competitor') totalCompetitors++;
    if (n.level === 'series') totalSeries++;
    n.children.forEach(walk);
  }

  walk(node);
  return { totalProducts, totalCompetitors, totalSeries };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function groupBy<T>(items: T[], keyFn: (item: T) => string): Record<string, T[]> {
  const result: Record<string, T[]> = {};
  for (const item of items) {
    const key = keyFn(item);
    (result[key] ??= []).push(item);
  }
  return result;
}

function pitchRange(specs: PriceIntelProductSpec[]): string {
  const pitches = specs.map((s) => s.pitch_mm).filter((p): p is number => p != null && p > 0);
  if (pitches.length === 0) return '—';
  const min = Math.min(...pitches);
  const max = Math.max(...pitches);
  if (min === max) return `P${min}`;
  return `P${min}–P${max}`;
}

function dominantValue(specs: PriceIntelProductSpec[], key: keyof PriceIntelProductSpec): string | null {
  const counts: Record<string, number> = {};
  for (const spec of specs) {
    const v = spec[key];
    if (typeof v === 'string' && v) counts[v] = (counts[v] ?? 0) + 1;
  }
  const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
  return sorted[0]?.[0] ?? null;
}

function buildSpecSummary(spec: PriceIntelProductSpec): string {
  const parts: string[] = [spec.model];
  if (spec.pitch_mm) parts.push(`pitch ${spec.pitch_mm}mm`);
  if (spec.led_type) parts.push(`${spec.led_type} 封装`);
  if (spec.brightness) parts.push(`${spec.brightness}nit`);
  if (spec.indoor_outdoor) parts.push(spec.indoor_outdoor);
  if (spec.panel_width_mm && spec.panel_height_mm) {
    parts.push(`箱体 ${spec.panel_width_mm}×${spec.panel_height_mm}mm`);
  }
  if (spec.weight_kg) parts.push(`${spec.weight_kg}kg`);
  return parts.join(', ');
}

function buildSeriesSummary(series: string, specs: PriceIntelProductSpec[]): string {
  const io = dominantValue(specs, 'indoor_outdoor');
  const led = dominantValue(specs, 'led_type');
  const parts = [`${series} 系列`, `${specs.length} 款产品`, pitchRange(specs)];
  if (io) parts.push(io);
  if (led) parts.push(`${led} 封装`);
  return parts.join(', ');
}

function buildCompetitorSummary(comp: string, specs: PriceIntelProductSpec[]): string {
  const seriesCount = new Set(specs.map((s) => s.series || '未分类')).size;
  return `${comp} 共 ${specs.length} 款产品，${seriesCount} 个系列，pitch 范围 ${pitchRange(specs)}`;
}
